import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BaseButton } from "../../../components/Button/BaseButton.tsx";
import { Button } from "../../../components/Button/Button.tsx";
import { PimentoStar } from "../../../components/Icon/assets/PimentoStar.tsx";
import { Icon, type IconName } from "../../../components/Icon/Icon.tsx";
import { Popover } from "../../../components/Popover/Popover.tsx";
import { Spinner } from "../../../components/Spinner/Spinner.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { BACKOFFICE, WORKSPACES } from "../../../routes.ts";
import { DebugModeToggle } from "../../components/DebugMode/DebugModeToggle.tsx";
import { useDebugMode } from "../../components/DebugMode/hooks/useDebugMode.ts";
import { useUser } from "../../hooks/useUser.ts";
import { useWorkspace } from "../../hooks/useWorkspace.ts";
import { useWorkspaceDetails } from "../../hooks/useWorkspaceDetails.ts";
import { isUserAllowedTo } from "../../utils/isUserAllowedTo.ts";
import { ProfileSettings } from "../ProfileSettings/ProfileSettings.tsx";
import { profileSettingsDialogStore } from "../ProfileSettings/stores/profileSettingsDialogStore.ts";

export const UserMenu = () => {
  const { user } = useUser();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();

  const { logout } = useAuth0();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { details, isLoading } = useWorkspaceDetails({
    enabled: isUserMenuOpen,
  });
  const { isDialogOpen: isProfileSettingsDialogOpen } =
    profileSettingsDialogStore.useState();
  const isDebugModeOn = useDebugMode();

  const createNewWorkspace = useAppMutation<{ uuid: string }>({
    path: "workspaces",
    invalidate: ["workspaces", "users/me"],
    onSuccess: ({ data }) => {
      navigate(`${WORKSPACES}/${data.uuid}/`);
    },
  }).mutation;
  return (
    <>
      <Popover
        isOpen={isUserMenuOpen}
        onOpenChange={(open) => {
          if (!isProfileSettingsDialogOpen) setIsUserMenuOpen(open);
        }}
        align="start"
        side="top"
        sideOffset={4}
        className="w-[256px] rounded-300"
        content={
          <div className="flex-col divide-y-025 divide-solid">
            <div className="flex-col gap-300 p-200">
              <div className="flex-row">
                <div className="flex-row flex-fill items-center gap-150">
                  <div className="flex-col-center rounded-150 p-50 w-500 h-500  bg-[#1A1A1A]">
                    <PimentoStar className="fill-[#F3F20E]" size={12} />
                  </div>
                  <div className="flex-col">
                    <div className="label-md-semibold text-primary">
                      {workspace.name}
                    </div>
                    {isLoading ? (
                      <Spinner size="xs" />
                    ) : (
                      <div className="label-sm-semibold text-secondary">
                        {workspace.type === "basic"
                          ? "Free"
                          : workspace.type.upperFirst()}{" "}
                        plan{" "}
                        {details?.num_members
                          ? details.num_members === 1
                            ? " • 1 member"
                            : ` • ${details.num_members} members`
                          : ""}
                      </div>
                    )}
                  </div>
                </div>
                <DebugModeToggle />
              </div>
              <Button
                onClick={() => {
                  profileSettingsDialogStore.openDialog();
                  profileSettingsDialogStore.setProfileSettingsTab("profile");
                }}
                variant="secondary"
                iconName="Settings"
                iconPosition="left"
                size="md"
              >
                <div className="label-md-default">Settings</div>
              </Button>
            </div>
            <div className="flex-col gap-050 p-300">
              <div className="text-disabled label-md-semibold truncate">
                {user?.email}
              </div>
              <div className="relative w-full">
                <div className="z-10 absolute top-0 left-0 w-full h-100 bg-gradient-to-b from-[var(--grey-1)]" />
                <div className="py-100 w-full max-h-[150px] overflow-y-auto">
                  <div className=" flex-col w-full h-full">
                    {user?.workspaces.map((item) => (
                      <BaseButton
                        key={item.uuid}
                        className={classNames(
                          "h-800 pl-150 pr-200 rounded-150 flex-fill text-primary ",
                          workspace.uuid === item.uuid
                            ? "label-md-semibold bg-surface-primary-active"
                            : "label-md-default bg-button-surface-secondary-rest hover:bg-button-surface-secondary-hover",
                        )}
                        onClick={() => {
                          navigate(`${WORKSPACES}/${item.uuid}/`);
                        }}
                      >
                        <div className="flex-row items-center gap-150">
                          <div className="flex-col-center rounded-150 p-50 w-500 h-500  bg-[#1A1A1A]">
                            <PimentoStar className="fill-[#F3F20E]" size={12} />
                          </div>
                          <div className="flex-fill">{item.name}</div>
                          {workspace.uuid === item.uuid && (
                            <Icon name="Check" size="sm" />
                          )}
                        </div>
                      </BaseButton>
                    ))}
                    <ClickableUserMenuItem
                      iconName="SquarePlus"
                      title="Create new workspace"
                      onClick={() =>
                        createNewWorkspace.mutate({
                          name: "New workspace",
                        })
                      }
                    />
                  </div>
                </div>
                <div className="z-10 absolute bottom-0 left-0 w-full h-100 bg-gradient-to-t from-[var(--grey-1)]" />
              </div>
            </div>
            <div className="flex-col p-300">
              {user &&
                isUserAllowedTo(user, "backoffice:edit") &&
                isDebugModeOn && (
                  <a href={BACKOFFICE} target="_blank">
                    <UserMenuItem
                      title="Backoffice"
                      iconName="Menu"
                      externalLink
                    />
                  </a>
                )}
              <a href="https://help.pimento.design" target="_blank">
                <UserMenuItem
                  iconName="LifeBuoy"
                  title="Help center"
                  externalLink
                />
              </a>
              <a
                href="https://pimentodesign.notion.site/terms-and-conditions"
                target="_blank"
              >
                <UserMenuItem
                  iconName="ScrollText"
                  title="Terms and conditions"
                  externalLink
                />
              </a>
              <ClickableUserMenuItem
                iconName="LogOut"
                title="Log out"
                onClick={() => logout({ returnTo: window.location.origin })}
              />
            </div>
          </div>
        }
      >
        <BaseButton
          className="pl-150 pr-200 py-300 w-full flex-row items-center justify-between rounded-150 text-secondary
        label-md-default hover:bg-surface-primary-hover focus:text-primary focus:label-md-semibold
        focus:bg-surface-primary-active"
        >
          <div className="flex-row flex-fill items-center h-[22px] gap-150 ">
            <div className="flex-col-center rounded-150 p-50 w-500 h-500  bg-[#1A1A1A]">
              <PimentoStar className="fill-[#F3F20E]" size={12} />
            </div>
            <div className="flex-col flex-fill">
              <div className="label-md-semibold text-primary truncate">
                {workspace.name}
              </div>
              <div className="label-sm-semibold text-secondary truncate">
                {user?.first_name && user.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user?.email}
              </div>
            </div>
            <Icon name="ChevronDown" size="sm" />
          </div>
        </BaseButton>
      </Popover>
      <ProfileSettings />
    </>
  );
};
const UserMenuItem = ({
  title,
  iconName,
  externalLink = false,
}: {
  title: string;
  iconName: IconName;
  externalLink?: boolean;
}) => (
  <div
    className={classNames(
      "px-200 py-100 h-800 rounded-150 flex-row items-center gap-200 bg-surface-primary-rest hover:bg-surface-primary-hover",
    )}
  >
    <div className="flex-fill flex-row items-center gap-200">
      <div className="flex-row-center w-400">
        <Icon name={iconName} size="sm" />
      </div>
      <div className="label-md-default">{title}</div>
    </div>
    {externalLink && <Icon name="ExternalLink" size="sm" />}
  </div>
);

const ClickableUserMenuItem = ({
  title,
  iconName,
  onClick,
}: {
  title: string;
  iconName: IconName;
  onClick: () => void;
}) => (
  <BaseButton onClick={onClick}>
    <UserMenuItem title={title} iconName={iconName} />
  </BaseButton>
);
